import { useEffect, useRef, useState } from 'react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@teach/ui'
import { useMessages } from '@/i18n'
import { messages } from './messages'
import type { CourseWithoutBlocks } from '@/types/course'
import { useCourseLanguages } from '@/features/ai/useCourseLanguages'
import { useIsDraftSourceSettling } from '@/features/moderation/lib/draftSourceSettling'
import { useGetEditorCourseQuery, useSetTranslationPublishedMutation } from '../../coursesApi'
import {
  hasServerTranslationRow,
  isTranslationPublished,
  translationPublishKey,
} from '../../lib/courseTranslation'
import { useIsOwnRunActive } from '../studio/useBlocksInRun'
import { useSheetSwipe } from '../studio/useSheetSwipe'
import { useCourseRereadQueued } from '../course-settings/useCourseRereadQueued'
import type { Lang } from '@/types/lang'
import { DeleteLanguageDialog } from './DeleteLanguageDialog'
import { LangMenuItem } from './LangMenuItem'
import { LangTriggerButton } from './LangTriggerButton'
import { useLanguageActions } from './useLanguageActions'
import { useAvailableContentLangs } from './useAvailableContentLangs'
import { TranslationDialogs } from './translation/TranslationDialogs'
import { TranslationSection } from './translation/TranslationSection'
import { useMenuTranslation } from './translation/useMenuTranslation'

interface Props {
  course: CourseWithoutBlocks
  /** Телефон: кнопка-значок, а меню выезжает шторкой снизу. Содержимое то же: его пункты живут
   *  только внутри меню, а положение шторки задаёт `[data-lang-sheet]` в `index.css`. */
  sheet?: boolean
}

/**
 * Переключатель языка контента курса — реф-парити с LanguageSwitcher (oa-studio-prototype).
 * КАКОЙ язык базовый, выводится из course.sourceLang, и сменить его можно в любой момент
 * (§6.10): прежний базовый становится переводом, текст по слотам не переезжает. Язык
 * перевода тумблер показывает или скрывает у учеников; удаление — только в настройках курса. Статусы языков (чип у перевода,
 * точка наполненности у базового) считает сервер и отдаёт в `languages[]` сметы; меню
 * перезапрашивает её в момент открытия.
 *
 * Меню управляемое (`menuOpen`): действие, которому нужен диалог, сначала закрывает
 * меню, потом открывает диалог — а сам диалог живёт РЯДОМ с DropdownMenu, не внутри
 * DropdownMenuContent. Так же устроены диалоги перевода (`TranslationDialogs`):
 * запуск, прогресс и отмена перевода живут в подвале этого меню.
 */
export const ContentLangSwitcher = ({ course, sheet = false }: Props) => {
  const m = useMessages(messages)
  const {
    current,
    base: BASE_LANG,
    other: OTHER_LANG,
    otherAdded,
    available,
  } = useAvailableContentLangs(course)
  const languages = useCourseLanguages(course.id)
  const actions = useLanguageActions(course)
  const isRunActive = useIsOwnRunActive(course.id)
  // Проверка курса гасит все языковые действия: контент заморожен до решения, а
  // любое из них пишет в курс. Свой прогон гасит только пункты «⋯»: на смену базового и
  // удаление сервер ответит `409`, а добавление и видимость (§6.8, §6.12) он принимает.
  const switchDisabled = actions.isBusy || actions.isFrozen
  const actionsDisabled = isRunActive || switchDisabled
  // После смены базового статусы пересчитывает сервер: до ответа и перечитывания — скелетон.
  const { isFetching: courseFetching } = useGetEditorCourseQuery(course.id)
  const rereadQueued = useCourseRereadQueued(course.id, actions.writtenAt)
  // Первая запись в живой курс заводит черновик: чтение сначала отдаёт живую версию без неё.
  const sourceSettling = useIsDraftSourceSettling(course.id)
  const settling =
    actions.isBusy || courseFetching || rereadQueued || sourceSettling || languages.isFetching
  const [rebasing, setRebasing] = useState(false)
  // Добавление — две записи (объявить и показать), и чтение между ними отдаёт язык скрытым или
  // вовсе без него. Строку держим добавленной и видимой, пока всё не осядет; отказ покажет настоящее.
  const [enabling, setEnabling] = useState(false)
  useEffect(() => {
    if (settling) return
    setRebasing(false)
    setEnabling(false)
  }, [settling])
  const [menuOpen, setMenuOpen] = useState(false)
  const { sheetRef, handlers: swipeHandlers } = useSheetSwipe(() => setMenuOpen(false))
  const [deleting, setDeleting] = useState<Lang | null>(null)
  const [setPublished] = useSetTranslationPublishedMutation({
    fixedCacheKey: translationPublishKey(course.id),
  })
  // Тумблер добавляет язык СРАЗУ видимым: в меню он дальше значит «показан /
  // скрыт», и только что включённый язык не должен встать выключенным.
  // У живого курса публикация языка уходит в черновик (до «Обновить»), у
  // неопубликованного ученики его не увидят, пока курс не выйдет.
  const enableOther = async () => {
    setEnabling(true)
    if (await actions.add(OTHER_LANG))
      void setPublished({ courseId: course.id, lang: OTHER_LANG, isPublished: true })
  }
  // Диалог живёт ВНЕ DropdownMenuContent: закрытие меню размонтирует его
  // содержимое, и диалог, открытый изнутри, исчез бы вместе с ним.
  const translation = useMenuTranslation(
    course.id,
    isRunActive || actions.isBusy,
    () => setMenuOpen(false),
    sheet && menuOpen,
  )
  // Диалоги открываются из меню, а пункт, с которого их открыли, к закрытию
  // диалога размонтирован — Radix вернул бы фокус в body. Возвращаем на кнопку.
  const triggerRef = useRef<HTMLButtonElement>(null)
  const returnFocus = (e: Event) => {
    e.preventDefault()
    triggerRef.current?.focus()
  }

  // Фокус на кнопку возвращаем только с клавиатуры: после клика мимо меню браузер
  // рисовал на ней рамку фокуса, и она оставалась.
  const closedByEscape = useRef(false)
  const onCloseAutoFocus = (e: Event) => {
    if (!closedByEscape.current) e.preventDefault()
    closedByEscape.current = false
  }

  const onOpenChange = (open: boolean) => {
    setMenuOpen(open)
    // Спека §08: статусы пересчитываются в момент открытия меню.
    if (open) languages.refetch()
    else translation.onMenuClosed()
  }

  return (
    <>
      {sheet && menuOpen ? (
        <div
          aria-hidden
          className="fixed inset-0 z-40 bg-background/60 backdrop-blur-[2px] duration-200 animate-in fade-in"
        />
      ) : null}
      <DropdownMenu open={menuOpen} onOpenChange={onOpenChange}>
        <DropdownMenuTrigger asChild>
          <LangTriggerButton
            ref={triggerRef}
            label={translation.isRunning ? m.translationMenuRunning : m.translationMenu}
            isRunning={translation.isRunning}
            compact={sheet}
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          ref={sheet ? sheetRef : undefined}
          data-lang-sheet={sheet ? '' : undefined}
          className={
            sheet
              ? 'max-h-[85dvh] w-full overflow-y-auto rounded-b-none border-x-0 border-b-0 pb-[max(1.5rem,env(safe-area-inset-bottom))] data-[state=closed]:!slide-out-to-bottom-full data-[state=open]:!slide-in-from-bottom-full data-[state=closed]:!zoom-out-100 data-[state=open]:!zoom-in-100'
              : 'w-96'
          }
          onEscapeKeyDown={() => {
            closedByEscape.current = true
          }}
          onCloseAutoFocus={onCloseAutoFocus}
        >
          {/* Ручка, как у шторки блоков: за неё шторку смахивают вниз. */}
          {sheet ? (
            <div
              data-testid="lang-sheet-handle"
              className="touch-none pb-2 pt-1"
              {...swipeHandlers}
            >
              <div aria-hidden className="mx-auto h-1 w-10 rounded-full bg-foreground/20" />
            </div>
          ) : null}
          {available.map((id) => (
            <LangMenuItem
              key={id}
              id={id}
              isBase={id === BASE_LANG}
              state={languages.stateOf(id)}
              statusLoading={rebasing}
              courseId={course.id}
              isPublished={enabling || rebasing || isTranslationPublished(course, id)}
              actionsDisabled={actionsDisabled}
              actionsHint={isRunActive ? m.actionsDuringRun : null}
              switchDisabled={switchDisabled}
              onMakeBase={() => {
                setRebasing(true)
                void actions.makeBase(id)
              }}
              onDelete={
                id !== BASE_LANG && hasServerTranslationRow(course, id, false)
                  ? () => {
                      setMenuOpen(false)
                      setDeleting(id)
                    }
                  : undefined
              }
            />
          ))}
          {otherAdded ? null : enabling ? (
            <LangMenuItem
              id={OTHER_LANG}
              isBase={false}
              state={languages.stateOf(OTHER_LANG)}
              courseId={course.id}
              isPublished
              actionsDisabled
              switchDisabled
              onMakeBase={() => undefined}
            />
          ) : (
            // Невключённый язык виден сразу — с выключенным тумблером, как во вкладке
            // «Язык» настроек. Включение объявляет его и переключает редактор.
            <LangMenuItem
              id={OTHER_LANG}
              isBase={false}
              enabled={false}
              state={null}
              courseId={course.id}
              isPublished={false}
              actionsDisabled={actionsDisabled}
              switchDisabled={switchDisabled}
              onMakeBase={() => undefined}
              onEnable={() => void enableOther()}
            />
          )}
          {/* Порядок макета CEO: языки → «Скоро» → черта → подсказка про
              базовый → объём и кнопка перевода. Подсказка стоит ПЕРЕД
              переводом: она объясняет, откуда он берётся. */}
          <DropdownMenuSeparator />
          {/* OTHER_LANG здесь — язык перевода: `translationLangOf(course)`
              совпадает с ним по построению. */}
          {otherAdded || enabling ? (
            <TranslationSection {...translation.section} targetLang={OTHER_LANG} />
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
      <TranslationDialogs {...translation.dialogs} onCloseAutoFocus={returnFocus} />
      <DeleteLanguageDialog
        lang={deleting}
        isDeleting={actions.isBusy}
        onOpenChange={(open) => !open && setDeleting(null)}
        onConfirm={() => {
          const lang = deleting
          setDeleting(null)
          if (lang) void actions.remove(lang)
        }}
      />
    </>
  )
}
